import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../components/Header';
import HeroSection from '../components/HeroSection';
import NotReadyBar from '../components/NotReadyBar';
import HowWeWorkSection from '../components/HowWeWorkSection';
import ReviewsSection from '../components/ReviewsSection';
import AboutSection from '../components/AboutSection';
import CredentialsValuesSection from '../components/CredentialsValuesSection';
import ProgramsSection from '../components/ProgramsSection';
import KalkulatorokSection from '../components/KalkulatorokSection';
import LeadMagnetSection from '../components/LeadMagnetSection';
// import TestimonialsSection from '../components/TestimonialsSection'; // Hidden - reactivate when real client success stories are available
import BlogSection from '../components/BlogSection';
import ContactSection from '../components/ContactSection';
import Footer from '../components/Footer';
import BookingModal from '../components/BookingModal';
import PromoPopupModal from '../components/PromoPopupModal';
import usePromoPopup from '../hooks/usePromoPopup';
import { trackLead } from '../utils/metaPixel';

const HomePage = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const location = useLocation();

  const openBooking = () => setIsBookingOpen(true);

  // Homepage-only promo popup — 1.5s delay + 1 day cooldown per visitor.
  // Content comes from the admin doc (/api/promo-popup/active) falling
  // back to /app/frontend/src/config/promoPopup.js.
  const promoPopup = usePromoPopup({ enabled: true });
  const handlePromoCta = () => {
    // Fire a Meta Pixel Lead event so the coach can attribute the popup
    // to real bookings in the pixel dashboard. `content_name` makes it
    // easy to filter to just this campaign.
    trackLead({ content_name: `promo_popup:${promoPopup.promo?.version || 'default'}` });
    promoPopup.reportClick();
    promoPopup.close();
    // Mark this booking flow as coming from the promo — the BookingModal
    // reads this to show a hint under the 60-min option.
    // Link típusú promo (PDF / minta étrend) → átnavigálunk, nem foglalás.
    if (promoPopup.promo?.cta_action === 'link' && promoPopup.promo?.cta_href) {
      window.location.assign(promoPopup.promo.cta_href);
      return;
    }
    // Open the booking flow (same modal the "Időpontfoglalás" nav button opens).
    try { sessionStorage.setItem('oc_from_promo_cta', promoPopup.promo?.version || '1'); } catch { /* ignore */ }
    setIsBookingOpen(true);
  };

  // Smooth-scroll to in-page anchors when arriving from any other route
  // (e.g. clicking the "Időpontfoglalás" CTA on a blog post -> /#contact).
  // React Router's navigate() doesn't natively scroll to hash. We retry a
  // few times because the page is heavy (images + calculator) and the
  // target section may still be mounting when navigation completes.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    let attempts = 0;
    let timer;
    const tryScroll = () => {
      const el = document.getElementById(id);
      attempts += 1;
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // One more re-align after lazy images settle the layout
        setTimeout(() => {
          const el2 = document.getElementById(id);
          if (el2) el2.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 600);
        return;
      }
      if (attempts < 8) {
        timer = setTimeout(tryScroll, 150);
      }
    };
    timer = setTimeout(tryScroll, 80);
    return () => clearTimeout(timer);
  }, [location.hash, location.key]);

  return (
    <div className="min-h-screen">
      <Header onBookClick={openBooking} />
      <HeroSection onGetStarted={openBooking} />
      <NotReadyBar />
      <HowWeWorkSection onBookClick={openBooking} />
      <ReviewsSection />
      <AboutSection onBookClick={openBooking} />
      <ProgramsSection onBookClick={openBooking} />
      <KalkulatorokSection onBookClick={openBooking} />
      <LeadMagnetSection />
      <CredentialsValuesSection />
      {/* <TestimonialsSection /> */}
      <BlogSection />
      <ContactSection />
      <Footer />
      <BookingModal 
        isOpen={isBookingOpen} 
        onClose={() => setIsBookingOpen(false)} 
      />
      <PromoPopupModal
        open={promoPopup.isOpen}
        promo={promoPopup.promo}
        onCtaClick={handlePromoCta}
        onClose={promoPopup.close}
      />
    </div>
  );
};

export default HomePage;
